import React, { useState, useEffect } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styled, { keyframes } from 'styled-components';
import { navDelay, loaderDelay } from '@utils';
import { usePrefersReducedMotion } from '@hooks';
import { useLanguage } from '@contexts/LanguageContext';
import { getTranslation } from '@utils/i18n';

// Animasyonlar
const gradientShift = keyframes`
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
`;

const glow = keyframes`
  0%, 100% {
    text-shadow: 0 0 20px rgba(100, 255, 218, 0.5),
                 0 0 40px rgba(100, 255, 218, 0.3),
                 0 0 60px rgba(100, 255, 218, 0.2);
  }
  50% {
    text-shadow: 0 0 30px rgba(100, 255, 218, 0.8),
                 0 0 60px rgba(100, 255, 218, 0.5),
                 0 0 90px rgba(100, 255, 218, 0.3);
  }
`;

const StyledHeroSection = styled.section`
  ${({ theme }) => theme.mixins.flexCenter};
  flex-direction: column;
  align-items: flex-start;
  min-height: 100vh;
  height: 100vh;
  padding: 0;

  @media (max-height: 700px) and (min-width: 700px), (max-width: 360px) {
    height: auto;
    padding-top: var(--nav-height);
  }

  h1 {
    margin: 0 0 30px 4px;
    color: var(--green);
    font-family: var(--font-mono);
    font-size: clamp(var(--fz-sm), 5vw, var(--fz-md));
    font-weight: 400;
    position: relative;
    display: inline-block;
    transition: all 0.3s ease;

    @media (max-width: 480px) {
      margin: 0 0 20px 2px;
    }

    &::after {
      content: '';
      position: absolute;
      bottom: -5px;
      left: 0;
      width: 0;
      height: 2px;
      background: linear-gradient(90deg, var(--green), transparent);
      transition: width 0.5s ease;
    }

    &:hover {
      transform: translateX(3px);

      &::after {
        width: 100%;
      }
    }
  }

  h2.big-heading {
    position: relative;
    background: linear-gradient(
      135deg,
      var(--lightest-slate) 0%,
      var(--light-slate) 50%,
      var(--lightest-slate) 100%
    );
    background-size: 200% 200%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${gradientShift} 5s ease infinite;
    transition: all 0.3s ease;

    &:hover {
      animation: ${glow} 2s ease-in-out infinite;
      transform: translateY(-2px);
    }
  }

  h3 {
    margin-top: 5px;
    color: var(--slate);
    line-height: 0.9;
    transition: all 0.3s ease;

    &:hover {
      color: var(--lightest-slate);
      transform: translateX(5px);
    }
  }

  p {
    margin: 20px 0 0;
    max-width: 540px;
    line-height: 1.6;
    transition: all 0.3s ease;

    &:hover {
      transform: translateX(3px);
    }
  }

  .email-link {
    ${({ theme }) => theme.mixins.bigButton};
    margin-top: 50px;
    position: relative;
    overflow: hidden;
    transition: all 0.3s ease;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(100, 255, 218, 0.2), transparent);
      transition: left 0.5s ease;
    }

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 10px 30px rgba(100, 255, 218, 0.3);

      &::before {
        left: 100%;
      }
    }

    &:active {
      transform: translateY(-1px);
    }
  }
`;

const Hero = () => {
  const [isMounted, setIsMounted] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();
  const { language } = useLanguage();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    const timeout = setTimeout(() => setIsMounted(true), navDelay);
    return () => clearTimeout(timeout);
  }, []);

  const one = <h1>{getTranslation(language, 'hero.greeting')}</h1>;
  const two = <h2 className="big-heading">{getTranslation(language, 'hero.name')}</h2>;
  const three = <h3 className="big-heading">{getTranslation(language, 'hero.title')}</h3>;
  const four = (
    <>
      <p>{getTranslation(language, 'hero.description')}</p>
    </>
  );
  const five = (
    <a className="email-link" href="mailto:edanurnair@gmail.com">
      {getTranslation(language, 'hero.getInTouch')}
    </a>
  );

  const items = [one, two, three, four, five];

  return (
    <StyledHeroSection>
      {prefersReducedMotion ? (
        <>
          {items.map((item, i) => (
            <div key={i}>{item}</div>
          ))}
        </>
      ) : (
        <TransitionGroup component={null}>
          {isMounted &&
            items.map((item, i) => (
              <CSSTransition key={i} classNames="fadeup" timeout={loaderDelay}>
                <div style={{ transitionDelay: `${i + 1}00ms` }}>{item}</div>
              </CSSTransition>
            ))}
        </TransitionGroup>
      )}
    </StyledHeroSection>
  );
};

export default Hero;
